import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User } from '@/types/auth';
import { authService } from '@/services/AuthService';
import { Role } from '@/types/enums';
import { useNavigate } from 'react-router-dom';

/**
 * Interfaz para el contexto de autenticación
 */
interface AuthContextType {
  // Estado
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  
  // Acciones
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  hasRole: (role: Role) => boolean;
  hasAnyRole: (roles: Role[]) => boolean;
}

/**
 * Contexto de autenticación
 */
const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Props del AuthProvider
 */
interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Provider del contexto de autenticación
 * Maneja el estado global de autenticación y proporciona métodos para login/logout
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  /**
   * Inicializa el estado de autenticación al cargar la aplicación
   */
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        setIsLoading(true);
        
        // Verificar si hay un token válido
        const isValid = await authService.verifyToken();
        
        if (isValid) {
          const currentUser = authService.getUser();
          setUser(currentUser);
        } else {
          setUser(null);
        }
      } catch (error) {
        console.error('Error inicializando autenticación:', error);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  /**
   * Realiza el login del usuario
   */
  const login = async (email: string, password: string): Promise<void> => {
    try {
      setIsLoading(true);
      
      const response = await authService.login({ email, password });
      setUser(response.user);
      
      // Si necesita cambiar la contraseña, ProtectedRoute muestra el cambio obligatorio
      navigate('/');
    } catch (error) {
      console.error('Error en login:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Cierra la sesión del usuario
   */
  const logout = async (): Promise<void> => {
    try {
      setIsLoading(true);
      
      await authService.logout();
      setUser(null);
      navigate('/login');
    } catch (error) {
      console.error('Error en logout:', error);
      // Limpiar estado local aunque falle el logout en servidor
      setUser(null);
      navigate('/login');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Verifica si el usuario tiene un rol específico
   */
  const hasRole = (role: Role): boolean => {
    return authService.hasRole(role);
  };

  /**
   * Verifica si el usuario tiene alguno de los roles especificados
   */
  const hasAnyRole = (roles: Role[]): boolean => {
    return authService.hasAnyRole(roles);
  };

  /**
   * Actualiza la información del usuario actual desde el servidor
   */
  const refreshUser = async (): Promise<void> => {
    try {
      const currentUser = await authService.getCurrentUser();
      setUser(currentUser);
    } catch (error) {
      console.error('Error actualizando usuario:', error);
      // Si falla la actualización, mantener el usuario actual
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    refreshUser,
    hasRole,
    hasAnyRole
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Hook personalizado para usar el contexto de autenticación
 * @returns Contexto de autenticación
 * @throws Error si se usa fuera del AuthProvider
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  
  if (context === undefined) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  
  return context;
};

export default AuthContext;